import React, { useState, useEffect } from 'react';

import StepWizard, { StepWizardProps } from 'react-step-wizard';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import { useAppSelector } from '@hooks/statehooks';

import Nav from './nav';
import ProgressBar from './progressbar';
import SideBar from '@components/customise/sidebar/sidebar';

import Options from '@components/customise/option/options';
import Paint from '@components/customise/option/paint';
import Trim from '@components/customise/option/trim';
import Hood from '@components/customise/option/hood';

import '@styles/customise.scss';
import '@styles/customise/wizard.scss';
import '@styles/customise/transitions.scss';

import { StepWizardOrder } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/stepwizardorder';
import { StepDetailsProps, StepWizardChildProps } from 'interface';

import cn from 'classnames';

/**
 * The Step Wizard component
 *
 * @returns The Step Wizard component
 */
const Wizard: React.FC = () => {
  const { colours, options } = useAppSelector((state) => state.selectedOptions);
  const [state, updateState] = useState({
    instance: {},
    isMobile: false,
    activeStep: 0,
  });

  const [stepOrder, setStepOrder] = useState<StepWizardOrder[] | []>([]);

  let transitions = {
    enterRight: 'animated enterRight',
    enterLeft: 'animated enterLeft',
    exitRight: 'animated exitRight',
    exitLeft: 'animated exitLeft',
    intro: 'animated intro',
  };

  const { isMobile } = useDeviceDetect();

  let steps: any = [];

  const addStep = (data: any[], label: string, required: boolean = false) => {
    if (data.length > 0)
      steps.push({ name: label, items: data.length, required: required });
  };

  const buildSteps = () => {
    addStep(colours.paint, 'Exterior Paint', true);
    addStep(colours.trim, 'Interior Fabric', true);
    addStep(colours.hood, 'Hood', true);
    addStep(options, 'Options');
  };

  useEffect(() => {
    buildSteps();

    setStepOrder(
      steps.map((option: any, index: number) =>
        Object.assign({
          order: index + 1,
          name: option.name,
          items: option.items ? option.items : 0,
          required: option.required,
        })
      )
    );
  }, []);

  const onStepChange = (stepDetails: StepDetailsProps) => {
    updateState({
      ...state,
      ...stepDetails,
    });
  };

  const setInstance = (instance: StepWizardProps) =>
    updateState({
      ...state,
      instance,
      isMobile,
    });

  const { instance } = state;

  return (
    <>
      <ProgressBar
        stepWizard={instance as StepWizardChildProps}
        stepOrder={stepOrder}
      />
      <div className="customise-wrapper">
        <div
          className={cn(
            'customise-options',
            isMobile ? 'customise-options-mobile' : ''
          )}
        >
          <section
            className="customise-options__wizard wide-component-container"
            data-testid="customise-options"
            data-cy="customise-wizard"
          >
            <StepWizard
              onStepChange={onStepChange}
              transitions={!isMobile ? transitions : {}}
              instance={setInstance}
              className="step-wizard"
              isHashEnabled
            >
              {colours.paint && colours.paint.length > 0 && (
                <Paint
                  options={colours.paint}
                  currentStep={(instance as StepWizardChildProps).currentStep}
                  totalSteps={(instance as StepWizardChildProps).totalSteps}
                  hashKey={'exterior-paint'}
                ></Paint>
              )}
              {colours.trim && colours.trim.length > 0 && (
                <Trim
                  options={colours.trim}
                  currentStep={(instance as StepWizardChildProps).currentStep}
                  totalSteps={(instance as StepWizardChildProps).totalSteps}
                  hashKey={'interior-fabric'}
                />
              )}
              {colours.hood && colours.hood.length > 0 && (
                <Hood
                  options={colours.hood}
                  currentStep={(instance as StepWizardChildProps).currentStep}
                  totalSteps={(instance as StepWizardChildProps).totalSteps}
                  hashKey={'hood'}
                />
              )}
              {options &&
                options.length > 0 &&
                ((
                  <Options
                    options={options}
                    stepWizard={instance as StepWizardChildProps}
                    hashKey={'options'}
                  />
                ) as any)}
            </StepWizard>
          </section>
        </div>
        <SideBar />
      </div>
      <Nav
        stepWizard={instance as StepWizardChildProps}
        stepOrder={stepOrder}
      />
    </>
  );
};

export default Wizard;
